import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../App';
import RegisterContactPage from './RegisterContactPage';
import { isBlogAllowed } from './Blog';
import { 
  Activity,
  AlertCircle,
  BarChart3,
  CheckCircle2,
  Clock,
  FileText,
  Heart,
  Mail,
  Package,
  Phone,
  Search,
  Shield,
  Stethoscope,
  Truck,
  Users,
  ArrowRight,
  Building2,
  Calendar,
  TrendingUp,
  ShoppingCart,
  UserCheck,
  RefreshCw,
  PieChart,
  MessageSquare,
  ClipboardList,
  ChevronDown,
  Zap,
  Target,
  CheckCircle,
  PlayCircle,
  Menu,
  X,
  Euro,
  TrendingDown,
  PhoneOff,
  Gauge,
  Send,
  User,
  Rocket,
  Star
} from 'lucide-react';
import './HomePage.css';

const API_URL = process.env.REACT_APP_API_URL;

const BlogPreview = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const navigate = useNavigate();

  // No mostrar si el blog no está permitido en este dominio
  const blogAllowed = isBlogAllowed();

  useEffect(() => {
    if (!blogAllowed) return;
    const fetchLatestPosts = async () => {
      try {
        const res = await fetch(`${API_URL}/api/blog/posts?limit=3&page=1`);
        const data = await res.json();
        setBlogPosts(data.posts || []);
      } catch (err) {
        console.error('Error fetching blog preview:', err);
      }
    };
    fetchLatestPosts();
  }, [blogAllowed]);

  if (!blogAllowed || blogPosts.length === 0) return null;

  return (
    <section className="blog-preview-section" id="blog">
      <div className="container">
        <div className="section-header observe-me">
          <h2>Últimas publicaciones del Blog</h2>
          <p>Mantente al día con noticias, novedades y recursos de CentralSalud</p>
        </div>

        <div className="blog-preview-grid observe-me">
          {blogPosts.map(post => (
            <div
              key={post.id}
              className="blog-preview-card"
              onClick={() => navigate(`/blog/${post.slug}`)}
              style={{ cursor: 'pointer' }}
            >
              <div className="blog-preview-image">
                {post.imagen_portada ? (
                  <img src={post.imagen_portada} alt={post.titulo} />
                ) : (
                  <div className="blog-preview-placeholder">
                    <FileText size={28} />
                  </div>
                )}
                {post.categoria_nombre && (
                  <span
                    className="blog-preview-cat"
                    style={{ backgroundColor: post.categoria_color }}
                  >
                    {post.categoria_nombre}
                  </span>
                )}
              </div>
              <div className="blog-preview-body">
                <h3>{post.titulo}</h3>
                {post.extracto && <p>{post.extracto}</p>}
                <div className="blog-preview-meta">
                  <span>
                    <Calendar size={13} />
                    {post.published_at && new Date(post.published_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                  </span>
                  <span className="blog-preview-read">Leer más <ArrowRight size={13} /></span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="blog-preview-cta observe-me">
          <button onClick={() => navigate('/blog')} className="btn-blog-cta">
            Ver todas las publicaciones <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </section>
  );
};

const HomePage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, login } = useContext(AuthContext);
  
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeFAQ, setActiveFAQ] = useState(-1);
  const [activeStep, setActiveStep] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Estados para el slider de ahorro
  const [monthlySpending, setMonthlySpending] = useState(5000);
  const [savings, setSavings] = useState(750);

  const countersAnimated = useRef(false);
  
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/gestion-clientes');
    }
  }, [isAuthenticated, navigate]);

  // Auto-cycle through process steps
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % 4);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Calcular ahorro cuando cambia el gasto mensual
  useEffect(() => {
    const calculatedSavings = monthlySpending * 0.15; // 15% de ahorro
    setSavings(calculatedSavings);
  }, [monthlySpending]);

  // Intersection Observer para animaciones
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
          
          if (entry.target.classList.contains('stats-container') && !countersAnimated.current) {
            animateCounters();
          }
        }
      });
    }, { threshold: 0.1 });

    const elements = document.querySelectorAll('.observe-me');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const animateCounters = () => {
    if (countersAnimated.current) return;
    countersAnimated.current = true;

    const counters = document.querySelectorAll('.counter');
    counters.forEach((counter) => {
      const target = parseInt(counter.getAttribute('data-target'));
      const increment = target / 60;
      let current = 0;

      const updateCounter = () => {
        if (current < target) {
          current += increment;
          counter.textContent = Math.ceil(current);
          setTimeout(updateCounter, 30);
        } else {
          counter.textContent = target;
        }
      };
      updateCounter();
    });
  };

  const toggleFAQ = (index) => {
    setActiveFAQ(activeFAQ === index ? -1 : index);
  };
  
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Error al iniciar sesión');
      }
      
      login(data.token, data.user);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGetStarted = () => {
    navigate('/register');
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const processSteps = [
    {
      icon: <ShoppingCart size={32} />,
      title: "Realiza tu Pedido",
      description: "Desde tu dashboard, selecciona productos y crea pedidos fácilmente"
    },
    {
      icon: <Zap size={32} />,
      title: "Procesamos Automáticamente",
      description: "Nuestro sistema envía automáticamente tu pedido al proveedor asignado"
    },
    {
      icon: <RefreshCw size={32} />,
      title: "Seguimiento en Vivo",
      description: "Recibe notificaciones del estado de tu pedido"
    },
    {
      icon: <CheckCircle size={32} />,
      title: "Entrega Confirmada",
      description: "El pedido llega a tu centro médico y actualizamos tu historial"
    }
  ];

  return (
    <div className="centralsalud-app">
      {/* Header Landing */}
      <header className="landing-header">
        <div className="container">
          <nav className="nav-landing">
            <div className="nav-brand">
              <img src="./logo.png" alt="CentralSalud Logo" className="nav-logo" />
            </div>

            {/* Desktop Navigation */}
            <div className="nav-menu desktop-nav">
              <button onClick={() => scrollToSection('inicio')} className="nav-link">
                Inicio
              </button>
              <button onClick={() => scrollToSection('proceso')} className="nav-link">
                Cómo Funciona
              </button>
              <button onClick={() => scrollToSection('caracteristicas')} className="nav-link">
                Características
              </button>
              <button onClick={() => scrollToSection('soluciones')} className="nav-link">
                Soluciones
              </button>
              <button onClick={() => scrollToSection('faq')} className="nav-link">
                FAQ
              </button>
              <button onClick={() => navigate('/blog')} className="nav-link">
                Blog
              </button>
            </div>

            {/* Desktop Actions */}
            <div className="nav-actions desktop-nav">
              <button onClick={() => scrollToSection('login')} className="btn-nav-secondary">
                Iniciar Sesión
              </button>
              <button onClick={() => scrollToSection('demo')} className="btn-nav-primary">
                Solicitar Demo
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="mobile-menu-btn"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </nav>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="mobile-nav">
              <div className="mobile-nav-links">
                <button onClick={() => scrollToSection('inicio')} className="mobile-nav-link">
                  Inicio
                </button>
                <button onClick={() => scrollToSection('proceso')} className="mobile-nav-link">
                  Cómo Funciona
                </button>
                <button onClick={() => scrollToSection('caracteristicas')} className="mobile-nav-link">
                  Características
                </button>
                <button onClick={() => scrollToSection('soluciones')} className="mobile-nav-link">
                  Soluciones
                </button>
                <button onClick={() => scrollToSection('faq')} className="mobile-nav-link">
                  FAQ
                </button>
                <button onClick={() => { navigate('/blog'); setMobileMenuOpen(false); }} className="mobile-nav-link">
                  Blog
                </button>
              </div>
              <div className="mobile-nav-actions">
                <button onClick={() => scrollToSection('login')} className="btn-mobile-secondary">
                  Iniciar Sesión
                </button>
                <button onClick={() => scrollToSection('demo')} className="btn-mobile-primary">
                  Solicitar Demo
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-medical" id="inicio">
        <div className="hero-background"></div>
        <div className="hero-overlay"></div>
        <div className="container">
          <div className="hero-main">
            
            <h1 className="hero-title observe-me">
              La conexión inteligente entre 
              <span className="gradient-text"> centros médicos</span> y 
              <span className="gradient-text"> proveedores</span>
            </h1>
            
            <p className="hero-subtitle observe-me">
              Revolucionamos la gestión de pedidos médicos eliminando la complejidad.
            </p>

            {/* Nuevos botones CTA */}
            <div className="hero-actions observe-me">
              <button onClick={() => scrollToSection('demo')} className="btn-primary-hero">
                <Rocket size={20} />
                Solicitar Demo Gratuita
              </button>
              <button onClick={() => scrollToSection('login')} className="btn-secondary-hero">
                <ArrowRight size={20} />
                Iniciar Sesión
              </button>
            </div>

          </div>

          <div className="hero-visual observe-me">
            <div className="connection-diagram">
              <div className="medical-center">
                <div className="center-icon">
                  <Building2 size={24} />
                </div>
                <span>Centro Médico</span>
              </div>
              
              <div className="connection-flow">
                <div className="flow-line"></div>
                <div className="centralsalud-hub">
                  <Heart size={20} />
                  <span>CentralSalud</span>
                </div>
                <div className="flow-line"></div>
              </div>
              
              <div className="supplier">
                <div className="supplier-icon">
                  <Package size={24} />
                </div>
                <span>Proveedor</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="process-section" id="proceso">
        <div className="container">
          <div className="section-header observe-me">
            <h2>El proceso más simple del sector médico</h2>
            <p>Cuatro pasos para transformar completamente tu gestión de pedidos</p>
          </div>

          <div className="process-timeline observe-me">
            {processSteps.map((step, index) => (
              <div 
                key={index} 
                className={`process-step ${activeStep === index ? 'active' : ''}`}
                onClick={() => setActiveStep(index)}
              >
                <div className="step-number">{index + 1}</div>
                <div className="step-content-wrapper">
                  <div className="step-icon">{step.icon}</div>
                  <div className="step-content">
                    <h3>{step.title}</h3>
                    <p>{step.description}</p>
                  </div>
                </div>
                {index < processSteps.length - 1 && <div className="step-connector-vertical"></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-premium" id="caracteristicas">
        <div className="container-wide">
          <div className="features-header observe-me">
            <h2>Características que marcan la diferencia</h2>
            <p>Soluciones integrales diseñadas para optimizar cada aspecto de tu gestión médica</p>
          </div>

          <div className="features-premium-grid observe-me">
            <div className="feature-premium-card savings">
              <div className="feature-premium-visual">
                <img 
                  src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2026&q=80"
                  alt="Ahorro de costes"
                />
                <div className="feature-premium-overlay">
                  <div className="feature-premium-icon">
                    <Euro size={32} />
                  </div>
                </div>
              </div>
              <div className="feature-premium-content">
                <h3>Ahorro de Costes</h3>
                <p>Gracias al volumen de compras que gestionamos desde nuestra central, negociamos con proveedores condiciones y precios mucho más favorables que los que una empresa puede conseguir por sí sola. Además, analizamos continuamente sus patrones de compra para detectar oportunidades o mejoras que reduzcan aún más sus costes.</p>
                <div className="feature-premium-stats">
                  <div className="premium-stat">
                    <span className="stat-value">15%</span>
                    <span className="stat-label">Ahorro Promedio</span>
                  </div>
                  <div className="premium-stat">
                    <span className="stat-value">50+</span>
                    <span className="stat-label">Proveedores</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="feature-premium-card tracking">
              <div className="feature-premium-visual">
                <img 
                  src="https://images.unsplash.com/photo-1586281380349-632531db7ed4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
                  alt="Seguimiento completo de pedidos"
                />
                <div className="feature-premium-overlay">
                  <div className="feature-premium-icon">
                    <Truck size={32} />
                  </div>
                </div>
              </div>
              <div className="feature-premium-content">
                <h3>Seguimiento Completo de Pedidos</h3>
                <p>Nos encargamos de gestionar cada pedido desde que se realiza hasta que llega a su destino. Esto incluye confirmar la recepción del pedido, anticipar posibles retrasos y mantenerle informado en todo momento.</p>
                <div className="feature-premium-benefits">
                  <div className="premium-benefit">
                    <CheckCircle size={16} />
                    <span>Confirmación automática</span>
                  </div>
                  <div className="premium-benefit">
                    <CheckCircle size={16} />
                    <span>Alertas en tiempo real</span>
                  </div>
                  <div className="premium-benefit">
                    <CheckCircle size={16} />
                    <span>Predicción de retrasos</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="feature-premium-card claims">
              <div className="feature-premium-visual">
                <img 
                  src="https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2126&q=80"
                  alt="Gestión de reclamaciones"
                />
                <div className="feature-premium-overlay">
                  <div className="feature-premium-icon">
                    <Shield size={32} />
                  </div>
                </div>
              </div>
              <div className="feature-premium-content">
                <h3>Gestión de Reclamaciones</h3>
                <p>Si alguna entrega presenta problemas, como productos dañados, retrasos o faltantes, nosotros tomamos la responsabilidad de gestionar la reclamación con el proveedor.</p>
                <div className="feature-premium-guarantee">
                  <div className="guarantee-badge">
                    <Rocket size={18} />
                    <span>Resolución en 24h</span>
                  </div>
                  <div className="guarantee-text">
                    <strong>100% sin preocupaciones</strong> - Tu tiempo es valioso, el nuestro también.
                  </div>
                </div>
              </div>
            </div>

            <div className="feature-premium-card stock">
              <div className="feature-premium-visual">
                <img 
                  src="https://images.unsplash.com/photo-1587560699334-cc4ff634909a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
                  alt="Ayuda ante faltas de stock"
                />
                <div className="feature-premium-overlay">
                  <div className="feature-premium-icon">
                    <RefreshCw size={32} />
                  </div>
                </div>
              </div>
              <div className="feature-premium-content">
                <h3>Ayuda ante Faltas de Stock</h3>
                <p>En caso de que algún producto no esté disponible o se agote en el proveedor habitual, nos encargamos de buscar alternativas rápidas ya sea a través de otros proveedores o con soluciones temporales. Nuestro objetivo es evitar que la falta de stock interrumpa su operación o genere retrasos.</p>
                <div className="feature-premium-network">
                  <div className="network-item">
                    <div className="network-number">95%</div>
                    <div className="network-label">Disponibilidad garantizada</div>
                  </div>
                  <div className="network-arrow">→</div>
                  <div className="network-item">
                    <div className="network-number">0h</div>
                    <div className="network-label">Tiempo de inactividad</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="solutions-hex" id="soluciones">
        <div className="container">
          <div className="solutions-header observe-me">
            <h2>Diseñado para cada tipo de usuario</h2>
            <p>Soluciones específicas que se adaptan a tu forma de trabajar</p>
          </div>

          <div className="hex-grid observe-me">
            <div className="hex-item medical-centers">
              <div className="hex-shape">
                <div className="hex-content">
                  <div className="hex-icon">
                    <Stethoscope size={48} />
                  </div>
                  <h3>Centros Médicos</h3>
                  <p>Gestiona todos tus pedidos. Reportes de consumo, análisis de costos y seguimiento completo.</p>
                  <ul>
                    <li>Dashboard personalizado</li>
                    <li>Gestión de múltiples proveedores</li>
                    <li>Reportes de consumo automáticos</li>
                    <li>Alertas de stock crítico</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="hex-item intermediation">
              <div className="hex-shape">
                <div className="hex-content">
                  <div className="hex-icon">
                    <Heart size={48} />
                  </div>
                  <h3>Intermediación CentralSalud</h3>
                  <p>Gestionamos y optimizamos la comunicación entre centros y proveedores.</p>
                  <ul>
                    <li>Gestión automática de pedidos</li>
                    <li>Resolución de incidencias</li>
                    <li>Optimización de precios</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="hex-item suppliers">
              <div className="hex-shape">
                <div className="hex-content">
                  <div className="hex-icon">
                    <Package size={48} />
                  </div>
                  <h3>Proveedores</h3>
                  <p>Recibe pedidos automáticamente, gestiona tu inventario y analiza la demanda de tus clientes médicos.</p>
                  <ul>
                    <li>Recepción automática de pedidos</li>
                    <li>Análisis de demanda por cliente</li>
                    <li>Optimización de precios y ofertas</li>
                    <li>Soporte técnico </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-creative">
        <div className="container">
          <div className="stats-grid observe-me stats-container">
            <div className="stat-card primary">
              <div className="stat-icon">
                <Building2 size={40} />
              </div>
              <div className="stat-content">
                <div className="stat-number">
                  <span className="counter" data-target="50">0</span>+
                </div>
                <div className="stat-label">Centros Médicos</div>
                <div className="stat-description">Confían en nuestra plataforma</div>
              </div>
            </div>

            <div className="stat-card secondary">
              <div className="stat-icon">
                <ShoppingCart size={40} />
              </div>
              <div className="stat-content">
                <div className="stat-number">
                  <span className="counter" data-target="190">0</span>+
                </div>
                <div className="stat-label">Pedidos Procesados</div>
                <div className="stat-description">En un mes</div>
              </div>
            </div>

            <div className="stat-card success">
              <div className="stat-icon">
                <TrendingUp size={40} />
              </div>
              <div className="stat-content">
                <div className="stat-number">
                  <span className="counter" data-target="99">0</span>%
                </div>
                <div className="stat-label">Satisfacción</div>
                <div className="stat-description">De nuestros usuarios</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Benefits Section with Slider */}
      <section className="pricing-benefits-section" id="demo">
        <div className="container">
          <div className="pricing-benefits-main observe-me">
            <div className="pricing-benefits-content">
              <div className="pricing-benefits-header">
                <h2>Ahorra tiempo y dinero gestionando tus pedidos</h2>
                <p>Con CentralSalud podrás controlar tus pedidos, supervisar proveedores, optimizar costos y reducir tiempos administrativos.</p>
              </div>

              {/* Slider de ahorro */}
              <div className="savings-calculator observe-me" style={{
                background: 'white',
                borderRadius: '20px',
                padding: '32px',
                marginBottom: '32px',
                border: '2px solid var(--primary-green)',
                boxShadow: '0 10px 40px rgba(60, 191, 154, 0.15)'
              }}>
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                  <h3 style={{ 
                    color: 'var(--text-primary)', 
                    fontSize: '1.4rem', 
                    fontWeight: '600',
                    marginBottom: '8px'
                  }}>
                    Calcula tu ahorro mensual
                  </h3>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '1rem' }}>
                    Ajusta tu gasto mensual actual y descubre cuánto podrías ahorrar
                  </p>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label style={{
                    display: 'block',
                    fontWeight: '600',
                    color: 'var(--text-primary)',
                    marginBottom: '12px',
                    fontSize: '1rem'
                  }}>
                    Gasto mensual actual: {formatCurrency(monthlySpending)}
                  </label>
                  
                  <input
                    type="range"
                    min="1000"
                    max="50000"
                    step="500"
                    value={monthlySpending}
                    onChange={(e) => setMonthlySpending(Number(e.target.value))}
                    style={{
                      width: '100%',
                      height: '8px',
                      borderRadius: '5px',
                      background: `linear-gradient(to right, var(--primary-green) 0%, var(--primary-green) ${((monthlySpending - 1000) / (50000 - 1000)) * 100}%, var(--gray-200) ${((monthlySpending - 1000) / (50000 - 1000)) * 100}%, var(--gray-200) 100%)`,
                      outline: 'none',
                      cursor: 'pointer',
                      appearance: 'none'
                    }}
                  />
                  
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '0.85rem',
                    color: 'var(--text-light)',
                    marginTop: '8px'
                  }}>
                    <span>1.000€</span>
                    <span>50.000€</span>
                  </div>
                </div>

                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  background: 'var(--accent-mint)',
                  padding: '20px',
                  borderRadius: '12px',
                  border: '1px solid rgba(60, 191, 154, 0.2)'
                }}>
                  <div>
                    <div style={{ 
                      fontSize: '0.9rem', 
                      color: 'var(--text-secondary)',
                      fontWeight: '500' 
                    }}>
                      Tu ahorro mensual con CentralSalud:
                    </div>
                    <div style={{ 
                      fontSize: '2rem', 
                      fontWeight: '900', 
                      color: 'var(--primary-green)' 
                    }}>
                      {formatCurrency(savings)}
                    </div>
                    <div style={{ 
                      fontSize: '0.85rem', 
                      color: 'var(--text-light)' 
                    }}>
                      ({formatCurrency(savings * 12)} al año)
                    </div>
                  </div>
                  <div style={{
                    background: 'var(--primary-green)',
                    color: 'white',
                    padding: '12px 20px',
                    borderRadius: '50px',
                    fontWeight: '600',
                    fontSize: '1.1rem'
                  }}>
                    15% menos
                  </div>
                </div>
              </div>

            </div>

            <div className="form-wrapper">
              <RegisterContactPage />
            </div>

          </div>
        </div>
        
        {/* Estilo para el slider */}
        <style jsx>{`
          input[type="range"]::-webkit-slider-thumb {
            appearance: none;
            height: 20px;
            width: 20px;
            border-radius: 50%;
            background: var(--primary-green);
            cursor: pointer;
            box-shadow: 0 0 0 3px rgba(60, 191, 154, 0.3);
            transition: all 0.3s ease;
          }
          
          input[type="range"]::-webkit-slider-thumb:hover {
            transform: scale(1.2);
            box-shadow: 0 0 0 5px rgba(60, 191, 154, 0.3);
          }
          
          input[type="range"]::-moz-range-thumb {
            height: 20px;
            width: 20px;
            border-radius: 50%;
            background: var(--primary-green);
            cursor: pointer;
            border: none;
            box-shadow: 0 0 0 3px rgba(60, 191, 154, 0.3);
          }
        `}</style>
      </section>

      {/* Blog Preview Section */}
      <BlogPreview />

      {/* FAQ Section */}
      <section className="faq-modern" id="faq">
        <div className="container">
          <div className="faq-header observe-me">
            <h2>Preguntas Frecuentes</h2>
            <p>Resolvemos tus dudas sobre la gestión de pedidos médicos</p>
          </div>

          <div className="faq-grid observe-me">
            <div className="faq-visual">
              <div className="faq-image-container">
                <img 
                  src="https://images.unsplash.com/photo-1559757148-5c350d0d3c56?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80"
                  alt="Centro médico moderno"
                />
              </div>
            </div>

            <div className="faq-list">
              {[
                {
                  question: "¿Cómo funciona CentralSalud como intermediario?",
                  answer: "Actuamos como el puente inteligente entre tu centro médico y los proveedores. Tú haces el pedido en nuestra plataforma, nosotros lo procesamos automáticamente, contactamos al proveedor adecuado, gestionamos todo el proceso y te mantenemos informado en tiempo real."
                },
                {
                  question: "¿Qué tipos de reportes y análisis obtengo?",
                  answer: "Consumo por producto , análisis de costos por proveedor, comparativas de precios, predicciones de demanda, tiempo de entrega promedio y análisis de ahorro conseguido."
                },
                {
                  question: "¿Qué sucede si hay problemas con un pedido?",
                  answer: "Nuestro equipo de soporte gestiona automáticamente cualquier incidencia: retrasos, productos agotados, cambios de especificaciones. Te comunicamos la situación y las soluciones en tiempo real, sin que tengas que hacer nada."
                },
                {
                  question: "¿Tiene costo usar CentralSalud?",
                  answer: "CentralSalud funciona con una mensualidad asequible que incluye todas las funcionalidades avanzadas, soporte técnico especializado y gestión completa de pedidos. Para nuevos clientes, ofrecemos el primer mes completamente gratuito para que puedas probar la plataforma sin compromiso."
                }
              ].map((faq, index) => (
                <div key={index} className="faq-item">
                  <button
                    className={`faq-question ${activeFAQ === index ? 'active' : ''}`}
                    onClick={() => toggleFAQ(index)}
                  >
                    <span>{faq.question}</span>
                    <ChevronDown size={20} className={`faq-icon ${activeFAQ === index ? 'rotated' : ''}`} />
                  </button>
                  <div className={`faq-answer ${activeFAQ === index ? 'active' : ''}`}>
                    <div className="faq-answer-content">
                      {faq.answer}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Login Section */}
      <section className="login-integrated" id="login">
        <div className="container">
          <div className="login-main observe-me">
            <div className="login-visual">
              <div className="login-image">
                <img 
                  src="https://images.unsplash.com/photo-1587854692152-cbe660dbde88?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80"
                  alt="Gestión médica moderna"
                />
              </div>
              <div className="login-benefits">
                <h3>Tu centro de control médico</h3>
                <div className="benefits-list">
                  <div className="benefit-point">
                    <CheckCircle size={20} />
                    <span>Dashboard personalizado para tu centro</span>
                  </div>
                  <div className="benefit-point">
                    <CheckCircle size={20} />
                    <span>Gestión automática de pedidos</span>
                  </div>
                  <div className="benefit-point">
                    <CheckCircle size={20} />
                    <span>Reportes en tiempo real</span>
                  </div>
                  <div className="benefit-point">
                    <CheckCircle size={20} />
                    <span>Soporte especializado</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="login-form-container">
              <div className="login-header">
                <h2>Accede a tu plataforma</h2>
                <p>Gestiona todos tus pedidos médicos desde un solo lugar</p>
              </div>

              <form onSubmit={handleLogin} className="login-form-modern">
                {error && (
                  <div className="error-alert">
                    <AlertCircle size={18} />
                    {error}
                  </div>
                )}

                <div className="form-field">
                  <div className="field-icon">
                    <Mail size={18} />
                  </div>
                  <input 
                    placeholder="Usuario" 
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                  />
                </div>

                <div className="form-field">
                  <div className="field-icon">
                    <Shield size={18} />
                  </div>
                  <input 
                    type="password" 
                    placeholder="Contraseña" 
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>

                <div className="form-options">
                  <a href="#recuperar" className="forgot-link">¿Olvidaste tu contraseña?</a>
                </div>

                <button 
                  type="submit" 
                  className={`btn-login ${loading ? 'loading' : ''}`}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div className="spinner-login"></div>
                      Accediendo...
                    </>
                  ) : (
                    <>
                      <ArrowRight size={18} />
                      Acceder a mi cuenta
                    </>
                  )}
                </button>
              </form>

              <div className="login-footer">
                <p>¿Tu centro médico no tiene cuenta?</p>
                <button onClick={handleGetStarted} className="register-link">
                  Solicita acceso gratuito aquí
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer-medical">
        <div className="container">
          <div className="footer-main">
            <div className="footer-brand">
              <div className="footer-logo">
                <Heart size={32} />
                <span>CentralSalud</span>
              </div>
              <p>La plataforma que conecta el futuro de la gestión médica.</p>
              <div className="footer-contact">
                <div className="contact-method">
                  <Mail size={16} />
                  <span>soporte@centralsalud.es</span>
                </div>
                <div className="contact-method">
                  <Phone size={16} />
                  <span>+34 635 82 35 92</span>
                </div>
              </div>
            </div>

            <div className="footer-links">
              <div className="link-group">
                <h4>Plataforma</h4>
                <ul>
                  <li><button onClick={() => scrollToSection('proceso')}>Cómo Funciona</button></li>
                  <li><button onClick={() => scrollToSection('caracteristicas')}>Características</button></li>
                  <li><button onClick={() => scrollToSection('demo')}>Demo</button></li>
                  <li><button onClick={() => scrollToSection('login')}>Seguridad</button></li>
                </ul>
              </div>

              <div className="link-group">
                <h4>Soluciones</h4>
                <ul>
                  <li><button onClick={() => scrollToSection('demo')}>Centros Médicos</button></li>
                  <li><button onClick={() => scrollToSection('demo')}>Hospitales</button></li>
                  <li><button onClick={() => scrollToSection('demo')}>Clínicas</button></li>
                  <li><button onClick={() => scrollToSection('demo')}>Proveedores</button></li>
                </ul>
              </div>

              <div className="link-group">
                <h4>Soporte</h4>
                <ul>
                  <li><button onClick={() => scrollToSection('faq')}>Preguntas Frecuentes</button></li>
                  <li><button onClick={() => scrollToSection('login')}>Centro de Ayuda</button></li>
                  <li><button onClick={() => scrollToSection('demo')}>Contacto</button></li>
                </ul>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <p>&copy; 2025 CentralSalud. Revolucionando la gestión médica desde 2020.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;